/**
 * Every one-shot in the game, synthesised.
 *
 * Two primitives do almost all the work: `burst` (a window onto the shared
 * noise buffers through a swept filter) and `tone` (an oscillator through an
 * envelope, optionally saturated). A footstep is two bursts and a sub blip; a
 * hound's growl is the same primitives with a formant filter and a waveshaper
 * in the way. Everything is pitch- and filter-randomised per call, because the
 * fastest way to make a procedural game sound cheap is to let the player notice
 * that two footsteps were identical.
 */

import { TUNING } from '../core/config';
import { clamp } from '../core/math';
import type { SfxName, Vec2 } from '../core/types';
import type { AudioGraph, NoiseKind } from './graph';
import { MAX_AUDIBLE } from './spatial';
import { PRIORITY, type Voice } from './voices';

export interface PlayOptions {
  pos?: Vec2;
  /** Linear multiplier on the sound's own level. */
  gain?: number;
  /** Pitch multiplier. Frequencies scale; durations do not. */
  rate?: number;
}

interface SfxProfile {
  /** Voice lifetime floor in seconds; builders extend it as they schedule. */
  dur: number;
  priority: number;
  gain: number;
  /** Base reverb send before distance scaling. */
  send: number;
  /** Follow the listener while playing. Only worth it for long sounds. */
  track?: boolean;
}

const PROFILES: Record<SfxName, SfxProfile> = {
  'step-carpet': { dur: 0.35, priority: PRIORITY.step, gain: 0.5, send: 0.1 },
  'step-concrete': { dur: 0.45, priority: PRIORITY.step, gain: 0.5, send: 0.24 },
  'step-water': { dur: 0.6, priority: PRIORITY.step, gain: 0.5, send: 0.2 },
  'step-tile': { dur: 0.4, priority: PRIORITY.step, gain: 0.45, send: 0.5 },
  pickup: { dur: 0.5, priority: PRIORITY.world, gain: 0.5, send: 0.14 },
  'consume-drink': { dur: 1.4, priority: PRIORITY.world, gain: 0.6, send: 0.1 },
  'consume-food': { dur: 1.3, priority: PRIORITY.world, gain: 0.5, send: 0.1 },
  heartbeat: { dur: 0.8, priority: PRIORITY.ui, gain: 0.7, send: 0.05 },
  hurt: { dur: 0.9, priority: PRIORITY.ui, gain: 0.85, send: 0.16 },
  death: { dur: 4.2, priority: PRIORITY.ui, gain: 0.9, send: 0.5 },
  'flashlight-click': { dur: 0.2, priority: PRIORITY.world, gain: 0.45, send: 0.1 },
  'flashlight-dying': { dur: 1.1, priority: PRIORITY.world, gain: 0.45, send: 0.12 },
  door: { dur: 1.4, priority: PRIORITY.world, gain: 0.6, send: 0.32 },
  noclip: { dur: 3.2, priority: PRIORITY.ui, gain: 0.8, send: 0.6 },
  descend: { dur: 3.4, priority: PRIORITY.ui, gain: 0.75, send: 0.55 },
  'light-buzz-pop': { dur: 1.2, priority: PRIORITY.world, gain: 0.6, send: 0.3 },
  'distant-thud': { dur: 1.0, priority: PRIORITY.world, gain: 1.0, send: 0.7 },
  'distant-scrape': { dur: 1.6, priority: PRIORITY.world, gain: 0.95, send: 0.65 },
  'distant-voice': { dur: 1.2, priority: PRIORITY.world, gain: 0.9, send: 0.8 },
  'slide-settle': { dur: 2.2, priority: PRIORITY.world, gain: 0.8, send: 0.55 },
  'hound-growl': { dur: 1.8, priority: PRIORITY.entity, gain: 0.9, send: 0.3, track: true },
  'hound-scream': { dur: 1.5, priority: PRIORITY.entity, gain: 1.0, send: 0.35, track: true },
  'hound-step': { dur: 0.4, priority: PRIORITY.entity, gain: 0.7, send: 0.26 },
  'smiler-breath': { dur: 2.6, priority: PRIORITY.entity, gain: 0.75, send: 0.4, track: true },
  'smiler-laugh': { dur: 1.6, priority: PRIORITY.entity, gain: 0.6, send: 0.5, track: true },
  'partygoer-step': { dur: 0.4, priority: PRIORITY.entity, gain: 0.5, send: 0.18 },
  'centipede-surge': { dur: 2.4, priority: PRIORITY.entity, gain: 0.8, send: 0.45, track: true },
  whisper: { dur: 1.6, priority: PRIORITY.entity, gain: 0.6, send: 0.75 },
  'ui-select': { dur: 0.2, priority: PRIORITY.ui, gain: 0.3, send: 0.05 },
  'ui-confirm': { dur: 0.4, priority: PRIORITY.ui, gain: 0.34, send: 0.06 },
  'ui-back': { dur: 0.35, priority: PRIORITY.ui, gain: 0.3, send: 0.05 },
};

/** Small procedural details the ambience beds scatter around the listener. */
export type SpotKind = 'drip' | 'plink' | 'metal' | 'creak' | 'hiss';

interface BurstOpts {
  t0: number;
  noise?: NoiseKind;
  /** Buffer playback rate. Shifts the noise character, not the filter. */
  rate?: number;
  type?: BiquadFilterType;
  f0: number;
  f1?: number;
  q?: number;
  gain: number;
  attack: number;
  decay: number;
  hold?: number;
  /** Amplitude ripple in Hz. Reads as grain, rattle or syllables. */
  ripple?: number;
  rippleDepth?: number;
  /** Push through the shared soft-clip curve. */
  drive?: boolean;
}

interface ToneOpts {
  t0: number;
  type?: OscillatorType;
  f0: number;
  f1?: number;
  gain: number;
  attack: number;
  decay: number;
  hold?: number;
  detune?: number;
  lpf?: number;
  q?: number;
  drive?: boolean;
  /** [cents, hertz] */
  vibrato?: readonly [number, number];
}

export class Sfx {
  /** Pitch multiplier for the call in flight. Set by `play`, read by the primitives. */
  private pitch = 1;

  constructor(private readonly g: AudioGraph) {}

  play(name: SfxName, opts?: PlayOptions): void {
    const profile = PROFILES[name];
    if (!profile) return;
    const pos = opts?.pos;
    if (pos) {
      const dx = pos.x - this.g.listener.x;
      const dz = pos.z - this.g.listener.z;
      // Nothing to hear out here, and starting the voice would only cost a steal.
      if (dx * dx + dz * dz > MAX_AUDIBLE * MAX_AUDIBLE) return;
    }

    const v = this.g.pool.acquire({
      priority: profile.priority,
      duration: profile.dur,
      gain: profile.gain * (opts?.gain ?? 1),
      send: profile.send,
      pos: pos ?? null,
      track: profile.track === true,
    });
    if (!v) return;

    this.pitch = clamp(opts?.rate ?? 1, 0.25, 4);
    this.build(name, v, this.g.now + 0.005);
    this.pitch = 1;
  }

  /** Ambience detail: cheap, stealable, always positional. */
  spot(kind: SpotKind, pos: Vec2, gain = 1): void {
    const v = this.g.pool.acquire({
      priority: PRIORITY.ambient,
      duration: kind === 'metal' ? 2.6 : kind === 'creak' ? 1.4 : 0.5,
      gain,
      send: kind === 'hiss' ? 0.3 : 0.55,
      pos,
    });
    if (!v) return;
    const t0 = this.g.now + 0.005;
    const r = this.g.rng;
    switch (kind) {
      case 'drip':
        // The upward chirp is the collapsing bubble; without it a drip is a click.
        this.tone(v, { t0, f0: r.range(560, 900), f1: r.range(1500, 2200), gain: 0.3, attack: 0.001, decay: 0.05 });
        this.tone(v, { t0, f0: r.range(240, 340), f1: 150, gain: 0.16, attack: 0.001, decay: 0.09 });
        this.burst(v, { t0, type: 'bandpass', f0: r.range(2800, 4200), q: 6, gain: 0.1, attack: 0.0006, decay: 0.02 });
        break;
      case 'plink':
        this.tone(v, { t0, f0: r.range(800, 1200), f1: r.range(1900, 2600), gain: 0.22, attack: 0.001, decay: 0.06 });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: r.range(900, 1600),
          f1: 500,
          q: 1.2,
          gain: 0.14,
          attack: 0.003,
          decay: 0.16,
        });
        break;
      case 'metal': {
        // Inharmonic partials: a struck plate, not a bell.
        const base = r.range(330, 520);
        this.tone(v, { t0, f0: base, gain: 0.16, attack: 0.004, decay: 1.9 });
        this.tone(v, { t0, f0: base * 2.38, gain: 0.09, attack: 0.004, decay: 1.5, detune: 11 });
        this.tone(v, { t0, f0: base * 4.03, gain: 0.045, attack: 0.003, decay: 1.1, detune: -14 });
        this.burst(v, { t0, type: 'bandpass', f0: base * 3, q: 1.5, gain: 0.2, attack: 0.001, decay: 0.05 });
        break;
      }
      case 'creak': {
        const f = r.range(78, 132);
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: f,
          f1: f * r.range(1.1, 1.5),
          gain: 0.07,
          attack: 0.2,
          hold: 0.3,
          decay: 0.4,
          lpf: 1300,
          q: 7,
          vibrato: [45, r.range(4, 8)],
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: r.range(450, 800),
          f1: r.range(900, 1500),
          q: 6,
          gain: 0.12,
          attack: 0.25,
          hold: 0.2,
          decay: 0.5,
        });
        break;
      }
      case 'hiss':
        this.burst(v, {
          t0,
          type: 'highpass',
          f0: r.range(2600, 4200),
          q: 0.7,
          gain: 0.14,
          attack: 0.12,
          hold: 0.12,
          decay: 0.35,
        });
        break;
    }
  }

  /* ---------------------------------------------------------------- *
   * The catalogue
   * ---------------------------------------------------------------- */

  private build(name: SfxName, v: Voice, t0: number): void {
    const r = this.g.rng;
    switch (name) {
      case 'step-carpet': {
        // Dull, no transient to speak of. Carpet is the sound of nothing.
        const rate = r.range(0.86, 1.16);
        this.burst(v, {
          t0,
          noise: 'pink',
          rate,
          type: 'lowpass',
          f0: 760 * r.range(0.82, 1.2),
          f1: 300,
          q: 0.9,
          gain: 0.55,
          attack: 0.004,
          decay: 0.1,
        });
        this.tone(v, { t0, f0: 78 * r.range(0.9, 1.1), f1: 52, gain: 0.16, attack: 0.004, decay: 0.09 });
        break;
      }

      case 'step-concrete': {
        const rr = r.range(0.85, 1.18);
        this.burst(v, { t0, type: 'bandpass', f0: 2100 * rr, f1: 900, q: 1.1, gain: 0.36, attack: 0.001, decay: 0.035 });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'lowpass',
          f0: 1100 * rr,
          f1: 420,
          q: 0.8,
          gain: 0.3,
          attack: 0.002,
          decay: 0.17,
        });
        this.tone(v, { t0, f0: 95 * rr, f1: 60, gain: 0.1, attack: 0.002, decay: 0.07 });
        break;
      }

      case 'step-water': {
        const rr = r.range(0.88, 1.15);
        this.burst(v, { t0, type: 'highpass', f0: 900 * rr, f1: 2600, q: 0.7, gain: 0.4, attack: 0.002, decay: 0.19 });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'lowpass',
          f0: 520,
          f1: 220,
          q: 0.7,
          gain: 0.22,
          attack: 0.004,
          decay: 0.13,
        });
        // Water falling back after the foot lifts.
        const drops = r.int(1, 3);
        for (let i = 0; i < drops; i++) {
          const t = t0 + r.range(0.05, 0.24);
          this.tone(v, { t0: t, f0: r.range(900, 1600), f1: r.range(2000, 3000), gain: 0.09, attack: 0.001, decay: 0.03 });
        }
        break;
      }

      case 'step-tile': {
        // Almost all of this sound is its own reverb tail.
        const rr = r.range(0.88, 1.16);
        this.burst(v, { t0, type: 'bandpass', f0: 3000 * rr, f1: 1900, q: 2.2, gain: 0.34, attack: 0.0008, decay: 0.028 });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'lowpass',
          f0: 800 * rr,
          f1: 380,
          q: 0.8,
          gain: 0.16,
          attack: 0.002,
          decay: 0.07,
        });
        break;
      }

      case 'pickup':
        this.tone(v, { t0, type: 'triangle', f0: 620, f1: 880, gain: 0.16, attack: 0.006, decay: 0.16 });
        this.burst(v, {
          t0: t0 + 0.01,
          noise: 'pink',
          type: 'bandpass',
          f0: 1700,
          f1: 2600,
          q: 0.8,
          gain: 0.13,
          attack: 0.006,
          decay: 0.12,
        });
        break;

      case 'consume-drink': {
        let t = t0;
        for (let i = 0; i < 3; i++) {
          this.tone(v, { t0: t, f0: 300 * r.range(0.88, 1.14), f1: 150, gain: 0.2, attack: 0.008, decay: 0.13 });
          this.burst(v, {
            t0: t,
            noise: 'pink',
            type: 'lowpass',
            f0: 900,
            f1: 400,
            q: 1.4,
            gain: 0.14,
            attack: 0.01,
            decay: 0.14,
          });
          t += r.range(0.26, 0.36);
        }
        break;
      }

      case 'consume-food': {
        let t = t0;
        const n = r.int(4, 6);
        for (let i = 0; i < n; i++) {
          this.burst(v, {
            t0: t,
            type: 'bandpass',
            f0: r.range(1400, 3000),
            f1: r.range(700, 1400),
            q: 1.6,
            gain: 0.2,
            attack: 0.001,
            decay: r.range(0.04, 0.1),
          });
          t += r.range(0.13, 0.25);
        }
        break;
      }

      case 'heartbeat':
        this.thump(v, t0, 1);
        this.thump(v, t0 + 0.21, 0.6);
        break;

      case 'hurt':
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 620,
          f1: 320,
          q: 2.4,
          gain: 0.32,
          attack: 0.006,
          decay: 0.3,
        });
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: 210,
          f1: 120,
          gain: 0.1,
          attack: 0.008,
          decay: 0.28,
          lpf: 900,
          drive: true,
        });
        this.tone(v, { t0, f0: 90, f1: 48, gain: 0.3, attack: 0.002, decay: 0.16 });
        break;

      case 'death':
        this.tone(v, { t0, f0: 96, f1: 20, gain: 0.5, attack: 0.05, decay: 3.0 });
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: 140,
          f1: 33,
          gain: 0.09,
          attack: 0.4,
          decay: 2.6,
          lpf: 420,
          drive: true,
        });
        this.burst(v, {
          t0,
          noise: 'brown',
          type: 'lowpass',
          f0: 700,
          f1: 90,
          q: 0.7,
          gain: 0.3,
          attack: 0.25,
          decay: 2.8,
        });
        // The ring that outlasts everything else.
        this.tone(v, { t0: t0 + 0.05, f0: 2400, f1: 1800, gain: 0.035, attack: 0.02, decay: 2.4 });
        break;

      case 'flashlight-click':
        this.burst(v, { t0, type: 'bandpass', f0: 2600, q: 3, gain: 0.3, attack: 0.0005, decay: 0.012 });
        this.burst(v, { t0: t0 + 0.014, type: 'bandpass', f0: 1750, q: 3, gain: 0.22, attack: 0.0005, decay: 0.02 });
        break;

      case 'flashlight-dying': {
        // A filament that cannot hold an arc.
        let t = t0;
        while (t < t0 + 0.9) {
          const d = r.range(0.02, 0.09);
          this.tone(v, {
            t0: t,
            type: 'sawtooth',
            f0: r.range(120, 175),
            gain: 0.09,
            attack: 0.002,
            decay: d,
            lpf: 1700,
            q: 3,
          });
          t += d + r.range(0.012, 0.07);
        }
        break;
      }

      case 'door':
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: 92,
          f1: 128,
          gain: 0.06,
          attack: 0.15,
          hold: 0.3,
          decay: 0.35,
          lpf: 1400,
          q: 6,
          vibrato: [40, 5.5],
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 500,
          f1: 1100,
          q: 5,
          gain: 0.14,
          attack: 0.2,
          hold: 0.2,
          decay: 0.4,
        });
        this.burst(v, { t0: t0 + 0.95, type: 'bandpass', f0: 2100, q: 2, gain: 0.28, attack: 0.0008, decay: 0.03 });
        this.tone(v, { t0: t0 + 0.95, f0: 130, f1: 80, gain: 0.16, attack: 0.002, decay: 0.1 });
        break;

      case 'noclip':
        // The floor stops being a floor.
        this.tone(v, { t0, f0: 340, f1: 32, gain: 0.4, attack: 0.03, decay: 1.9 });
        this.tone(v, { t0, type: 'triangle', f0: 505, f1: 47, gain: 0.11, attack: 0.05, decay: 1.7 });
        this.burst(v, { t0, type: 'bandpass', f0: 300, f1: 5000, q: 0.6, gain: 0.15, attack: 0.9, decay: 0.25 });
        this.burst(v, {
          t0: t0 + 1.5,
          noise: 'brown',
          type: 'lowpass',
          f0: 400,
          f1: 70,
          q: 0.7,
          gain: 0.3,
          attack: 0.02,
          decay: 1.4,
        });
        break;

      case 'descend':
        this.tone(v, { t0, f0: 44, f1: 26, gain: 0.42, attack: 0.6, hold: 0.4, decay: 1.8 });
        this.tone(v, { t0: t0 + 0.2, f0: 780, f1: 690, gain: 0.05, attack: 0.02, decay: 2.6 });
        this.tone(v, { t0: t0 + 0.2, f0: 1170, f1: 1035, gain: 0.026, attack: 0.02, decay: 2.2, detune: 9 });
        this.burst(v, {
          t0,
          noise: 'brown',
          type: 'lowpass',
          f0: 220,
          f1: 70,
          q: 0.6,
          gain: 0.2,
          attack: 0.5,
          decay: 2.0,
        });
        break;

      case 'light-buzz-pop': {
        // The ballast strains, then lets go.
        const f = TUNING.audio.humFrequency;
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: f * 2,
          gain: 0.1,
          attack: 0.35,
          hold: 0.05,
          decay: 0.06,
          lpf: 2600,
          q: 4,
        });
        const tp = t0 + 0.45;
        this.burst(v, { t0: tp, type: 'highpass', f0: 1200, f1: 4000, q: 0.7, gain: 0.5, attack: 0.0004, decay: 0.05 });
        this.tone(v, { t0: tp, f0: 220, f1: 90, gain: 0.2, attack: 0.001, decay: 0.07 });
        for (let i = 0; i < 3; i++) {
          this.burst(v, {
            t0: tp + 0.04 + i * r.range(0.03, 0.09),
            type: 'bandpass',
            f0: r.range(3200, 6000),
            q: 6,
            gain: 0.08,
            attack: 0.0006,
            decay: 0.06,
          });
        }
        break;
      }

      case 'distant-thud': {
        const rr = r.range(0.85, 1.2);
        this.tone(v, { t0, f0: 58 * rr, f1: 34, gain: 0.6, attack: 0.006, decay: 0.4 });
        this.burst(v, {
          t0,
          noise: 'brown',
          type: 'lowpass',
          f0: 260 * rr,
          f1: 110,
          q: 0.8,
          gain: 0.34,
          attack: 0.004,
          decay: 0.3,
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 900,
          f1: 400,
          q: 1.2,
          gain: 0.09,
          attack: 0.002,
          decay: 0.1,
        });
        break;
      }

      case 'distant-scrape':
        this.burst(v, {
          t0,
          noise: 'pink',
          rate: 0.7,
          type: 'bandpass',
          f0: 420,
          f1: 1500,
          q: 7,
          gain: 0.38,
          attack: 0.06,
          hold: 0.35,
          decay: 0.4,
          ripple: r.range(22, 34),
          rippleDepth: 0.55,
        });
        this.burst(v, {
          t0,
          noise: 'brown',
          type: 'lowpass',
          f0: 300,
          q: 0.8,
          gain: 0.16,
          attack: 0.1,
          hold: 0.3,
          decay: 0.4,
        });
        break;

      case 'slide-settle': {
        // A two-metre plastic tube with nothing in it. The character is the
        // hollowness: a low resonance an octave apart from itself, a long slow
        // swell rather than an onset, and no rattle at all — everything
        // `distant-scrape` has, this deliberately does not, so the two can
        // never be confused across a room.
        const f = r.range(96, 118);
        this.tone(v, { t0, f0: f, f1: f * 0.92, gain: 0.2, attack: 0.5, hold: 0.5, decay: 1.1 });
        this.tone(v, {
          t0: t0 + 0.06,
          type: 'triangle',
          f0: f * 2,
          f1: f * 1.86,
          gain: 0.06,
          attack: 0.4,
          hold: 0.4,
          decay: 1.2,
          detune: 7,
        });
        this.burst(v, {
          t0,
          noise: 'brown',
          type: 'bandpass',
          f0: f * 3.4,
          f1: f * 2.4,
          q: 2.2,
          gain: 0.09,
          attack: 0.45,
          hold: 0.4,
          decay: 1.0,
        });
        break;
      }

      case 'distant-voice': {
        // Two sliding formants. It is never a word, which is the point.
        const fa = r.range(380, 620);
        const fb = r.range(950, 1500);
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: fa,
          f1: fa * r.range(0.7, 1.4),
          q: 9,
          gain: 0.34,
          attack: 0.05,
          hold: 0.14,
          decay: 0.28,
        });
        this.burst(v, {
          t0: t0 + 0.03,
          noise: 'pink',
          type: 'bandpass',
          f0: fb,
          f1: fb * r.range(0.6, 1.5),
          q: 11,
          gain: 0.18,
          attack: 0.05,
          hold: 0.12,
          decay: 0.25,
        });
        const p = r.range(105, 190);
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: p,
          f1: p * r.range(0.8, 1.25),
          gain: 0.05,
          attack: 0.06,
          hold: 0.1,
          decay: 0.24,
          lpf: 700,
          q: 2,
          vibrato: [22, 5],
        });
        break;
      }

      case 'hound-growl': {
        // Saturated brown noise through a formant pair, rattling at ~23Hz.
        const rip = r.range(19, 27);
        this.burst(v, {
          t0,
          noise: 'brown',
          rate: 0.85,
          type: 'bandpass',
          f0: 165 * r.range(0.9, 1.12),
          f1: 210,
          q: 5.5,
          gain: 0.55,
          attack: 0.08,
          hold: 0.7,
          decay: 0.5,
          drive: true,
          ripple: rip,
          rippleDepth: 0.5,
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 640,
          f1: 880,
          q: 9,
          gain: 0.15,
          attack: 0.1,
          hold: 0.6,
          decay: 0.5,
          drive: true,
          ripple: rip,
          rippleDepth: 0.35,
        });
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: 54,
          f1: 66,
          gain: 0.14,
          attack: 0.12,
          hold: 0.6,
          decay: 0.5,
          lpf: 400,
          drive: true,
        });

        // The commit.
        //
        // Everything above is a flat rattle: it says something is there, but not
        // that it is about to move, so a player has no way to hear the wind-up
        // ending. This rides the tail and climbs — the only part of the growl
        // that rises — and its rattle speeds up with it.
        //
        // How much of it there is comes from the playback rate, which the hound
        // already ramps 0.82 -> 1.08 across a wind-up and drops to 0.7 for the
        // grumble it makes when it gives up. So the sweep grows pulse by pulse
        // as the thing makes up its mind, and the give-up has none of it at all,
        // without this needing to know anything about the behaviour.
        const commit = clamp((this.pitch - 0.78) / 0.3, 0, 1);
        if (commit > 0.02) {
          this.burst(v, {
            t0: t0 + 0.62,
            noise: 'brown',
            rate: 0.9,
            type: 'bandpass',
            f0: 230,
            f1: 300 + 260 * commit,
            q: 7,
            gain: 0.34 * commit,
            attack: 0.18,
            hold: 0.08,
            decay: 0.36,
            drive: true,
            ripple: rip * 1.35,
            rippleDepth: 0.3,
          });
        }
        break;
      }

      case 'hound-scream': {
        for (const d of [-15, 0, 18]) {
          this.tone(v, {
            t0,
            type: 'sawtooth',
            f0: 400 * r.range(0.95, 1.06),
            f1: 620,
            gain: 0.13,
            attack: 0.02,
            hold: 0.35,
            decay: 0.5,
            detune: d,
            lpf: 3000,
            q: 3,
            drive: true,
          });
        }
        this.burst(v, {
          t0,
          type: 'bandpass',
          f0: 1800,
          f1: 3400,
          q: 1.6,
          gain: 0.28,
          attack: 0.015,
          hold: 0.3,
          decay: 0.5,
          drive: true,
        });
        this.tone(v, { t0, f0: 110, f1: 150, gain: 0.2, attack: 0.02, hold: 0.3, decay: 0.4 });
        break;
      }

      case 'hound-step': {
        // Heavier and clawed. Nothing like the player's own step.
        const rr = r.range(0.88, 1.15);
        this.burst(v, { t0, type: 'bandpass', f0: 3400 * rr, q: 4, gain: 0.2, attack: 0.0006, decay: 0.02 });
        this.burst(v, {
          t0: t0 + 0.008,
          noise: 'brown',
          type: 'lowpass',
          f0: 420 * rr,
          f1: 160,
          q: 0.9,
          gain: 0.4,
          attack: 0.003,
          decay: 0.13,
        });
        this.tone(v, { t0, f0: 70 * rr, f1: 44, gain: 0.24, attack: 0.003, decay: 0.11 });
        break;
      }

      case 'smiler-breath':
        this.burst(v, {
          t0,
          noise: 'pink',
          rate: 0.7,
          type: 'lowpass',
          f0: 320,
          f1: 900,
          q: 1.1,
          gain: 0.26,
          attack: 0.75,
          decay: 0.55,
        });
        this.burst(v, {
          t0: t0 + 1.35,
          noise: 'pink',
          rate: 0.7,
          type: 'lowpass',
          f0: 800,
          f1: 260,
          q: 1,
          gain: 0.22,
          attack: 0.3,
          decay: 0.75,
        });
        // A hint that something in there is voiced.
        this.burst(v, {
          t0,
          noise: 'brown',
          type: 'bandpass',
          f0: 200,
          q: 4,
          gain: 0.08,
          attack: 0.6,
          hold: 0.5,
          decay: 0.9,
        });
        break;

      case 'smiler-laugh': {
        const rip = r.range(6.4, 8.6);
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 700,
          f1: 1150,
          q: 6,
          gain: 0.28,
          attack: 0.05,
          hold: 0.5,
          decay: 0.4,
          ripple: rip,
          rippleDepth: 0.85,
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 1600,
          f1: 2300,
          q: 8,
          gain: 0.11,
          attack: 0.06,
          hold: 0.45,
          decay: 0.35,
          ripple: rip,
          rippleDepth: 0.85,
        });
        this.tone(v, {
          t0,
          type: 'triangle',
          f0: 190,
          f1: 260,
          gain: 0.05,
          attack: 0.06,
          hold: 0.4,
          decay: 0.4,
          lpf: 900,
          vibrato: [60, rip],
        });
        break;
      }

      case 'partygoer-step': {
        // Built from the carpet-step synth with the numbers nudged a few
        // percent, so no two footfalls in a pack land quite the same way. The
        // wide random rate is what makes several of these fired close together
        // read as more than one set of feet.
        const rate = r.range(0.9, 1.1);
        this.burst(v, {
          t0,
          noise: 'pink',
          rate,
          type: 'lowpass',
          f0: 745 * r.range(0.86, 1.14),
          f1: 292,
          q: 0.92,
          gain: 0.55,
          attack: 0.005,
          decay: 0.11,
        });
        this.tone(v, { t0: t0 + 0.004, f0: 75 * r.range(0.92, 1.08), f1: 50, gain: 0.15, attack: 0.005, decay: 0.1 });
        break;
      }

      case 'centipede-surge': {
        const base = r.range(74, 96);
        this.tone(v, {
          t0,
          type: 'sawtooth',
          f0: base,
          f1: base * 0.72,
          gain: 0.1,
          attack: 0.35,
          hold: 0.6,
          decay: 0.9,
          lpf: 700,
          q: 2,
          vibrato: [35, r.range(3.4, 5)],
          drive: true,
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 330,
          f1: 250,
          q: 8,
          gain: 0.28,
          attack: 0.4,
          hold: 0.5,
          decay: 0.9,
        });
        this.burst(v, {
          t0,
          noise: 'pink',
          type: 'bandpass',
          f0: 1020,
          f1: 780,
          q: 11,
          gain: 0.13,
          attack: 0.45,
          hold: 0.4,
          decay: 0.8,
        });
        this.burst(v, {
          t0: t0 + 0.1,
          type: 'highpass',
          f0: 2400,
          q: 0.7,
          gain: 0.05,
          attack: 0.5,
          hold: 0.4,
          decay: 0.8,
        });
        break;
      }

      case 'whisper': {
        // Syllable-shaped sibilance. Close enough to speech that the player
        // will try to make out a word and fail.
        let t = t0;
        const n = r.int(3, 6);
        for (let i = 0; i < n; i++) {
          const hold = r.range(0.05, 0.15);
          const f = r.range(700, 2400);
          this.burst(v, {
            t0: t,
            type: 'bandpass',
            f0: f,
            f1: f * r.range(0.6, 1.6),
            q: 5,
            gain: 0.26,
            attack: 0.02,
            hold,
            decay: 0.09,
          });
          this.burst(v, {
            t0: t,
            noise: 'pink',
            type: 'bandpass',
            f0: r.range(300, 650),
            q: 8,
            gain: 0.11,
            attack: 0.03,
            hold: hold * 0.8,
            decay: 0.1,
          });
          t += hold + r.range(0.03, 0.12);
        }
        break;
      }

      case 'ui-select':
        this.tone(v, { t0, f0: 520, gain: 0.2, attack: 0.004, hold: 0.02, decay: 0.07 });
        break;

      case 'ui-confirm':
        this.tone(v, { t0, f0: 520, gain: 0.18, attack: 0.004, hold: 0.02, decay: 0.06 });
        this.tone(v, { t0: t0 + 0.08, f0: 780, gain: 0.18, attack: 0.004, hold: 0.03, decay: 0.14 });
        break;

      case 'ui-back':
        this.tone(v, { t0, f0: 520, gain: 0.16, attack: 0.004, hold: 0.02, decay: 0.05 });
        this.tone(v, { t0: t0 + 0.07, f0: 330, gain: 0.16, attack: 0.004, hold: 0.02, decay: 0.12 });
        break;
    }
  }

  /** One half of a heartbeat: a pitch-dropping sine with a noise body. */
  private thump(v: Voice, t: number, amp: number): void {
    this.tone(v, { t0: t, f0: 52, f1: 27, gain: 0.5 * amp, attack: 0.006, decay: 0.19 });
    this.burst(v, { t0: t, noise: 'brown', type: 'lowpass', f0: 180, q: 0.7, gain: 0.2 * amp, attack: 0.004, decay: 0.12 });
  }

  /* ---------------------------------------------------------------- *
   * Primitives
   * ---------------------------------------------------------------- */

  private burst(v: Voice, o: BurstOpts): number {
    const ctx = this.g.ctx;
    const attack = Math.max(0.0006, o.attack);
    const hold = o.hold ?? 0;
    const decay = Math.max(0.008, o.decay);
    const end = o.t0 + attack + hold + decay;

    const buf = this.g.buffer(o.noise ?? 'white');
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.playbackRate.value = clamp((o.rate ?? 1) * this.pitch, 0.05, 8);

    const f = ctx.createBiquadFilter();
    f.type = o.type ?? 'bandpass';
    f.Q.value = o.q ?? 1;
    f.frequency.setValueAtTime(this.hz(o.f0), o.t0);
    if (o.f1 !== undefined) f.frequency.exponentialRampToValueAtTime(this.hz(o.f1), end);

    const g = ctx.createGain();
    envelope(g.gain, o.t0, o.gain, attack, hold, decay);

    src.connect(f);
    let tail: AudioNode = f;
    if (o.drive) {
      const ws = ctx.createWaveShaper();
      ws.curve = this.g.shaper;
      f.connect(ws);
      tail = ws;
    }
    tail.connect(g);
    this.terminate(v, g, o.ripple, o.rippleDepth, o.t0, end);

    src.start(o.t0, this.g.rng.range(0, Math.max(0.01, buf.duration - 0.6)));
    src.stop(end + 0.02);
    v.own(src);
    v.keepUntil(end + 0.05);
    return end;
  }

  private tone(v: Voice, o: ToneOpts): number {
    const ctx = this.g.ctx;
    const attack = Math.max(0.0006, o.attack);
    const hold = o.hold ?? 0;
    const decay = Math.max(0.008, o.decay);
    const end = o.t0 + attack + hold + decay;

    const osc = ctx.createOscillator();
    osc.type = o.type ?? 'sine';
    if (o.detune) osc.detune.value = o.detune;
    osc.frequency.setValueAtTime(this.hz(o.f0), o.t0);
    if (o.f1 !== undefined) osc.frequency.exponentialRampToValueAtTime(this.hz(o.f1), end);

    let tail: AudioNode = osc;
    if (o.lpf !== undefined) {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = this.hz(o.lpf);
      f.Q.value = o.q ?? 0.7;
      osc.connect(f);
      tail = f;
    }
    if (o.drive) {
      const ws = ctx.createWaveShaper();
      ws.curve = this.g.shaper;
      tail.connect(ws);
      tail = ws;
    }

    const g = ctx.createGain();
    envelope(g.gain, o.t0, o.gain, attack, hold, decay);
    tail.connect(g);
    g.connect(v.input);

    if (o.vibrato) {
      const [cents, rate] = o.vibrato;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = rate;
      const amp = ctx.createGain();
      amp.gain.value = cents;
      lfo.connect(amp);
      amp.connect(osc.detune);
      lfo.start(o.t0);
      lfo.stop(end + 0.02);
      v.own(lfo);
    }

    osc.start(o.t0);
    osc.stop(end + 0.02);
    v.own(osc);
    v.keepUntil(end + 0.05);
    return end;
  }

  /**
   * Route an envelope's output into the voice, optionally through a ripple
   * stage. The ripple has to be a second gain rather than an LFO summed onto
   * the envelope param, so that it scales the envelope instead of offsetting it.
   */
  private terminate(v: Voice, g: GainNode, ripple: number | undefined, depth = 0.5, t0: number, end: number): void {
    if (!ripple) {
      g.connect(v.input);
      return;
    }
    const ctx = this.g.ctx;
    const rg = ctx.createGain();
    rg.gain.value = 1 - depth;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = ripple;
    const amp = ctx.createGain();
    amp.gain.value = depth;
    lfo.connect(amp);
    amp.connect(rg.gain);
    lfo.start(t0);
    lfo.stop(end + 0.02);
    v.own(lfo);
    g.connect(rg);
    rg.connect(v.input);
  }

  private hz(f: number): number {
    return clamp(f * this.pitch, 10, 20000);
  }
}

/**
 * Linear attack, optional hold, exponential decay. Exponential ramps cannot
 * reach zero, so the tail lands on a near-silent value and is then pinned to
 * silence — without the pin the param would hold that residue forever.
 */
function envelope(p: AudioParam, t0: number, peak: number, attack: number, hold: number, decay: number): number {
  const top = Math.max(0.0002, peak);
  const end = t0 + attack + hold + decay;
  p.setValueAtTime(0.0001, t0);
  p.linearRampToValueAtTime(top, t0 + attack);
  if (hold > 0) p.setValueAtTime(top, t0 + attack + hold);
  p.exponentialRampToValueAtTime(0.0001, end);
  p.setValueAtTime(0, end);
  return end;
}
